/**
 * Stale-bundle recovery helpers.
 *
 * When a deploy ships, Vite emits new content-hashed chunk filenames
 * and the old ones are deleted from the server. A browser tab that was
 * loaded BEFORE the deploy is still running the previous build in
 * memory. The moment that old build touches something the new server no
 * longer has — a code-split chunk, or simply a render path that assumes
 * the old shape — it blows up:
 *
 *   - a dynamic import 404s/503s → "Failed to fetch dynamically imported
 *     module", OR
 *   - a component resolves to `undefined` → React error #130
 *     ("element type is invalid … got: undefined") → blank white page.
 *
 * The cure for all of these is the same: one full page reload pulls the
 * fresh index.html (nginx serves it `no-store`) which references the
 * current chunk hashes. These helpers centralise that reload plus a
 * sessionStorage cooldown so we never loop when the real problem is a
 * dead network rather than a stale build.
 *
 * Shared by `main.jsx` (global error / unhandledrejection listeners)
 * and `components/ErrorBoundary.jsx` (React render crashes — the only
 * place that can catch a #130).
 */

// Kept identical to the key `main.jsx` used before this module existed
// so an in-flight cooldown survives the refactor.
const RELOAD_KEY = 'taskmagix-chunk-error-reloaded-at'
const RELOAD_COOLDOWN_MS = 30_000

/** True when an error message looks like a failed chunk / dynamic-import
 *  load rather than an ordinary application error. */
export function isChunkLoadFailure(message) {
  if (!message) return false
  const m = String(message).toLowerCase()
  return (
    m.includes('loading chunk') ||
    m.includes('loading css chunk') ||
    m.includes('failed to fetch dynamically imported module') ||
    m.includes("can't find variable: chunk") ||
    m.includes('importing a module script failed')
  )
}

/** True if we triggered a recovery reload within the cooldown window.
 *  The ErrorBoundary uses this to stop reloading and show its manual
 *  fallback instead of looping forever on a crash a reload can't fix. */
export function reloadedRecently() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0')
    return Date.now() - last < RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

/** Force a one-time full reload to pull the fresh build. Returns true if
 *  it reloaded, false if it was suppressed by the cooldown (meaning a
 *  reload already happened recently and didn't help — the caller should
 *  surface a manual recovery UI instead). */
export function recoverFromStaleBundle(reason) {
  if (reloadedRecently()) return false
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage unavailable (private mode) — still attempt the reload.
  }
  console.warn(
    '[taskmagix] Detected stale-bundle failure, reloading. Reason:',
    reason,
  )
  window.location.reload()
  return true
}

/** Hard-recovery escape hatch for a wedged client: unregister every
 *  service worker, delete every Cache Storage bucket, then reload. This
 *  rescues a browser stuck on an OLD service worker installed before the
 *  current network-only one shipped — that SW can otherwise keep serving
 *  a stale shell that never updates. Wired to the "Clear cache and
 *  reload" button in the ErrorBoundary fallback. */
export async function clearClientCachesAndReload() {
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // Best effort — reload regardless so the user is never stranded.
  }
  // Clear the cooldown so the post-clear load is free to self-heal again
  // if it still needs to.
  try {
    sessionStorage.removeItem(RELOAD_KEY)
  } catch {
    /* ignore */
  }
  window.location.reload()
}
